import { areSetsEqual } from '@utils';
import type { Edges, EID, NID, Nodes } from './types';

/**
 * An immutable set of element IDs
 */

export class Elements {
  private _nodes: Nodes;
  private _edges: Edges;
  private _nodeOpacity: Map<NID, number> | undefined;
  private _edgeOpacity: Map<EID, number> | undefined;

  nodeOpacity(node: NID): number {
    const result = this._nodeOpacity?.get(node) ?? (this._nodes.has(node) ? 1 : 0);
    return result;
  }

  edgeOpacity(edge: EID): number {
    return this._edgeOpacity?.get(edge) ?? (this._edges.has(edge) ? 1 : 0);
  }

  private constructor(nodes: Nodes, edges: Edges) {
    this._nodes = nodes;
    this._edges = edges;
  }

  static make(nodes?: Nodes | NID[], edges?: Edges | EID[]): Elements {
    return new Elements(new Set(nodes), new Set(edges));
  }

  static fromNode(node: NID): Elements {
    return new Elements(new Set([node]), new Set());
  }

  static fromNodes(nodes: Nodes): Elements {
    return new Elements(new Set(nodes), new Set());
  }

  static fromEdge(edge: EID): Elements {
    return new Elements(new Set(), new Set([edge]));
  }

  static fromEdges(edges: Edges): Elements {
    return new Elements(new Set(), new Set(edges));
  }

  get nodes(): Nodes {
    return new Set(this._nodes);
  }

  get edges(): Edges {
    return new Set(this._edges);
  }

  /**
   * Gets the total size of the set.
   *
   * @returns {number} The sum of the sizes of nodes and edges in the graph.
   */
  get size(): number {
    return this._nodes.size + this._edges.size;
  }

  /**
   * @returns a boolean indicating whether the `Elements` contains no elements.
   */
  get isEmpty(): boolean {
    return this._nodes.size === 0 && this._edges.size === 0;
  }

  /**
   * Retrieves any node from the collection of nodes.
   *
   * @returns {NID | undefined} A node identifier (NID) if available, otherwise undefined.
   */
  get anyNode(): NID | undefined {
    return this._nodes.values().next().value;
  }

  /**
   * Retrieves any edge ID from the collection of edges.
   *
   * @returns {EID | undefined} An edge ID if available, otherwise `undefined`.
   */
  get anyEdge(): EID | undefined {
    return this._edges.values().next().value;
  }

  /**
   * Adds the given elements to this set.
   *
   * @param elements - The elements to be added.
   * @returns {Elements} The current instance.
   */
  adding(elements: Elements): Elements {
    return new Elements(
      this._nodes.union(elements._nodes),
      this._edges.union(elements._edges)
    );
  }

  /**
   * Deletes the specified elements from this set.
   *
   * @param elements - The elements to be deleted.
   * @returns {Elements} The current instance.
   */
  deleting(elements: Elements): Elements {
    return new Elements(
      this._nodes.difference(elements._nodes),
      this._edges.difference(elements._edges)
    );
  }

  /**
   * Inverts the current set of nodes and edges with the provided elements. In
   * other words, it removes the elements that are common between the two sets
   * and adds the elements that are unique to the provided set.
   *
   * @param elements - The elements to invert with the current set of nodes and
   * edges.
   * @returns {Elements} The current instance.
   */
  inverting(elements: Elements): Elements {
    return new Elements(
      this._nodes.symmetricDifference(elements._nodes),
      this._edges.symmetricDifference(elements._edges)
    );
  }

  /**
   * Appends a new NID to the end of the `Elements`'s nodes.
   *
   * @param node - The ID of the node to be added.
   * @returns {Elements} The current instance.
   */
  addingNode(node: NID): Elements {
    return new Elements(new Set(this._nodes).add(node), this._edges);
  }

  /**
   * Adds the given nodes to the `Elements`'s nodes.
   *
   * @param nodes - The nodes to be added to the graph.
   * @returns The updated Elements instance with the new nodes added.
   */
  addingNodes(nodes: Nodes): Elements {
    return new Elements(this._nodes.union(nodes), this._edges);
  }

  /**
   * Appends a new EID to the end of the `Elements`'s edges.
   *
   * @param edge - The ID of the edge to be added.
   * @returns {Elements} The current instance.
   */
  addingEdge(edge: EID): Elements {
    return new Elements(this._nodes, new Set(this._edges).add(edge));
  }

  /**
   * Adds the given edges to the `Elements`'s edges.
   *
   * @param edges - The edges to be added.
   * @returns The updated Elements instance with the new edges included.
   */
  addingEdges(edges: Edges): Elements {
    return new Elements(this._nodes, this._edges.union(edges));
  }

  /**
   * Removes a specific NID from the `Elements`'s nodes.
   *
   * @param node - The ID of the node to be removed.
   * @returns {Elements} The current instance.
   */
  deletingNode(node: NID): Elements {
    const nodes = new Set(this._nodes);
    nodes.delete(node);
    return new Elements(nodes, this._edges);
  }

  deletingNodes(nodes: Nodes): Elements {
    return new Elements(this._nodes.difference(nodes), this._edges);
  }

  clearingNodes(): Elements {
    return new Elements(new Set(), this._edges);
  }

  /**
   * Removes a specific EID from the `Elements`'s edges.
   *
   * @param edge - The ID of the edge to be removed.
   * @returns {Elements} The current instance.
   */
  deletingEdge(edge: EID): Elements {
    const edges = new Set(this._edges);
    edges.delete(edge);
    return new Elements(this._nodes, edges);
  }

  deletingEdges(edges: Edges): Elements {
    return new Elements(this._nodes, this._edges.difference(edges));
  }

  clearingEdges(): Elements {
    return new Elements(this._nodes, new Set());
  }

  /**
   * @returns a boolean indicating whether the `Elements` contains the specified element.
   */
  hasNode(node: NID): boolean {
    return this._nodes.has(node);
  }

  /**
   * @returns a boolean indicating whether the `Elements` contains a specific EID.
   */
  hasEdge(edge: EID): boolean {
    return this._edges.has(edge);
  }

  /**
   * @returns a boolean indicating whether the `Elements` contains exactly the same elements as the argument.
   */
  equals(other: Elements): boolean {
    return (
      areSetsEqual(this._nodes, other._nodes) && areSetsEqual(this._edges, other._edges)
    );
  }

  /**
   * @returns a new `Elements` containing all the elements in this `Elements` and also all the elements in the argument.
   */
  union(other: Elements): Elements {
    return new Elements(this._nodes.union(other._nodes), this._edges.union(other._edges));
  }

  /**
   * @returns a new `Elements` containing all the elements which are both in this `Elements` and in the argument.
   */
  intersection(other: Elements): Elements {
    return new Elements(
      this._nodes.intersection(other._nodes),
      this._edges.intersection(other._edges)
    );
  }

  /**
   * @returns a new `Elements` containing all the elements in this `Elements` which are not also in the argument.
   */
  difference(other: Elements): Elements {
    return new Elements(
      this._nodes.difference(other._nodes),
      this._edges.difference(other._edges)
    );
  }

  /**
   * @returns a new `Elements` containing all the elements which are in either this `Elements` or in the argument, but not in both.
   */
  symmetricDifference(other: Elements): Elements {
    return new Elements(
      this._nodes.symmetricDifference(other._nodes),
      this._edges.symmetricDifference(other._edges)
    );
  }

  /**
   * @returns a boolean indicating whether all the elements in this `Elements` are also in the argument.
   */
  isSubsetOf(other: Elements): boolean {
    return this._nodes.isSubsetOf(other._nodes) && this._edges.isSubsetOf(other._edges);
  }

  /**
   * @returns a boolean indicating whether all the elements in the argument are also in this `Elements`.
   */
  isSupersetOf(other: Elements): boolean {
    return other.isSupersetOf(this);
  }

  /**
   * @returns a boolean indicating whether this `Elements` has no elements in common with the argument.
   */
  isDisjointFrom(other: Elements): boolean {
    return (
      this._nodes.isDisjointFrom(other._nodes) && this._edges.isDisjointFrom(other._edges)
    );
  }

  /**
   * Converts the graph elements (nodes and edges) to a string representation for debugging.
   *
   * @returns A string that lists all nodes and edges in the graph.
   */
  toString(): string {
    const components: string[] = [];
    if (this._nodes.size > 0) {
      components.push(`Nodes: ${Array.from(this._nodes).join(', ')}`);
    }
    if (this._edges.size > 0) {
      components.push(`Edges: ${Array.from(this._edges).join(', ')}`);
    }
    if (components.length === 0) {
      return '<empty>';
    }
    return components.join(', ');
  }

  interp(end: Elements): (t: number) => Elements {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const start = this;

    const nodes = start._nodes.union(end._nodes);
    const nodeMutators = new Map<NID, (t: number) => number>();
    for (const node of nodes) {
      const atStart = start._nodes.has(node);
      const atEnd = end._nodes.has(node);
      if (atStart === atEnd) {
        // Persisting node
        continue;
      }
      if (atEnd) {
        // Entering node
        nodeMutators.set(node, (t: number) => t);
      }
      if (atStart) {
        // Exiting node
        nodeMutators.set(node, (t: number) => 1 - t);
      }
    }

    const edges = start._edges.union(end._edges);
    const edgeMutators = new Map<EID, (t: number) => number>();
    for (const edge of edges) {
      const atStart = start._edges.has(edge);
      const atEnd = end._edges.has(edge);
      if (atStart === atEnd) {
        // Persisting edge
        continue;
      }
      if (atEnd) {
        // Entering edge
        edgeMutators.set(edge, (t: number) => t);
      }
      if (atStart) {
        // Exiting edge
        edgeMutators.set(edge, (t: number) => 1 - t);
      }
    }

    return (t: number) => {
      if (t >= 1) {
        return end;
      }
      const middle = new Elements(nodes, edges);
      middle._nodeOpacity = new Map();
      middle._edgeOpacity = new Map();

      for (const [node, mutator] of nodeMutators) {
        middle._nodeOpacity.set(node, mutator(t));
      }

      for (const [edge, mutator] of edgeMutators) {
        middle._edgeOpacity.set(edge, mutator(t));
      }

      return middle;
    };
  }
}
